/**
 * =============================================================================
 * IDS CRM Web - the Complete Work Log body for a job (Phase 6 step 7, spec 6.3)
 * =============================================================================
 *
 * The POST /tasks/:id/work-log body, moved out of the Log Work window's
 * handleSubmit (TaskDetail.js) unchanged, so the saved Log Work can add its
 * own fields without touching the rest:
 *  - with draftId: log_work_draft_id, log_work_versions (every part's
 *    version, as the screen last had it) and log_work_file_ids (the photos
 *    and files uploaded into the saved Log Work). The server refuses the save
 *    with LOG_WORK_CHANGED when either no longer matches.
 *  - with carryOn (Carry on tomorrow): no template_checks and no
 *    asset_outcomes. The check forms and robot answers are kept for tomorrow
 *    in the saved Log Work, and nothing about the robots is settled today.
 *
 * With neither, the body is exactly what the window sent before (the unit
 * test compares it field for field).
 *
 * The builder only builds. Every check that can stop the save (the van check,
 * handover notes, complete check forms) stays in the window, before this is
 * called.
 * =============================================================================
 */

import { buildDailyVanCheckPayload } from '../components/DailyVanCheckSection';

// A robot with no sign-off recorded yet counts as Done with no note, as the
// window's robotOutcomeFor gives it (emptyRobotOutcome in TaskAssetCheckSection).
const defaultRobotOutcome = () => ({ outcome: 'done', note: '', health_score: '', follow_up: null });

const trimmed = (value) => String(value ?? '').trim();

/**
 * Each section with its robot's sign-off for this visit. The window passes
 * sections that already carry robotOutcome; robotOutcomes (by asset id) is
 * only a fallback for a caller that has not joined them.
 */
function withRobotOutcomes(sections, robotOutcomes) {
  return (Array.isArray(sections) ? sections : []).filter(Boolean).map(section => {
    if (section.robotOutcome !== undefined && section.robotOutcome !== null) return section;
    if (!section.isRobot) return { ...section, robotOutcome: null };
    const key = section.key || section.assetId;
    const fromMap = robotOutcomes && key ? robotOutcomes[key] : null;
    return { ...section, robotOutcome: fromMap || defaultRobotOutcome() };
  });
}

/** A robot marked Not done on a closing log needs no form, only a reason. */
export function skipsForm(section, closesWork) {
  return Boolean(closesWork && section && section.isRobot && section.robotOutcome
    && section.robotOutcome.outcome === 'not_done');
}

/**
 * One template_checks entry per robot section with a form to save: a closing
 * log sends every Done section's form, a handover (or a robot left Not done)
 * only those with answers or photos. Nothing to send stays null, as before.
 */
export function buildTemplateChecksPayload({ sections, closesWork, robotOutcomes } = {}) {
  const entries = withRobotOutcomes(sections, robotOutcomes)
    .filter(section => section.useCheckTemplate && ((closesWork && !skipsForm(section, closesWork)) || section.hasContent))
    .map(section => section.buildTemplateCheckEntry());
  return entries.length > 0 ? entries : null;
}

/**
 * Phase 6 step 4: a robot's follow-up ticket, sent inside its asset_outcomes
 * entry only when several robots are on the job and the engineer ticked it.
 * Blank boxes are left out: the server then words the ticket and takes the
 * job's priority ("Same as this job"). An empty object still asks for the
 * ticket.
 */
export function buildRobotFollowUp(robotOutcome, followUpPerRobot) {
  const followUp = robotOutcome?.follow_up;
  if (!followUpPerRobot || !followUp || !followUp.wanted) return undefined;
  return {
    subject: trimmed(followUp.subject) || undefined,
    description: trimmed(followUp.description) || undefined,
    priority: followUp.priority || undefined
  };
}

/**
 * Phase 3: a closing log settles every robot on the job - one entry per robot
 * section with Done / Not done, its reason and its health. A handover settles
 * nothing, and a job with no robot sends nothing.
 */
export function buildAssetOutcomesPayload({ sections, closesWork, followUpPerRobot, robotOutcomes } = {}) {
  if (!closesWork) return undefined;
  const entries = withRobotOutcomes(sections, robotOutcomes)
    .filter(section => section.isRobot)
    .map(section => ({
      asset_id: section.assetId,
      outcome: section.robotOutcome.outcome === 'not_done' ? 'not_done' : 'done',
      health_score: section.robotOutcome.health_score || undefined,
      note: trimmed(section.robotOutcome.note) || undefined,
      follow_up: buildRobotFollowUp(section.robotOutcome, followUpPerRobot)
    }));
  return entries.length > 0 ? entries : undefined;
}

/**
 * The Complete Work Log body (not yet JSON). Fields left undefined are dropped by
 * JSON.stringify, exactly as before.
 *
 * @param {object}   args
 * @param {object}   args.formData               The window's form state.
 * @param {object[]} args.sections               Check-form sections in job order, each with isRobot and robotOutcome.
 * @param {object}   [args.robotOutcomes]        Robot sign-offs by asset id (fallback only).
 * @param {boolean}  args.closesWork             The chosen outcome closes the job.
 * @param {boolean}  args.followUpPerRobot       Several robots: each raises its own follow-up.
 * @param {boolean}  args.isSiteWork             Site work takes its times from Job Stages.
 * @param {object}   args.dailyVanCheck          The van check state.
 * @param {object}   [args.travelRecord]         The Job Stages record being filed.
 * @param {string[]} args.additionalEngineerIds  The crew, the filer left out.
 * @param {boolean}  args.needsSecondVan
 * @param {object}   args.secondVan              { vehicle_id, driver_id }
 * @param {boolean}  args.secondVanCheckOpen
 * @param {object}   args.secondVanCheck
 * @param {string}   [args.draftId]              The saved Log Work being filed.
 * @param {boolean}  [args.carryOn]              Carry on tomorrow.
 * @param {object}   [args.versions]             { partKey: version } from useSavedLogWork().versionsForFiling().
 * @param {string[]} [args.fileIds]              From useSavedLogWork().fileIdsForFiling().
 */
export function buildLogWorkPayload({
  formData,
  sections,
  robotOutcomes,
  closesWork,
  followUpPerRobot,
  isSiteWork,
  dailyVanCheck,
  travelRecord,
  additionalEngineerIds,
  needsSecondVan,
  secondVan,
  secondVanCheckOpen,
  secondVanCheck,
  draftId,
  carryOn,
  versions,
  fileIds
}) {
  const crewIds = Array.isArray(additionalEngineerIds) ? additionalEngineerIds : [];
  const body = {
    ...formData,
    // Site-work timings come from Job Stages — derived server-side.
    started_at: isSiteWork ? undefined : new Date(formData.started_at).toISOString(),
    ended_at: isSiteWork ? undefined : (formData.ended_at ? new Date(formData.ended_at).toISOString() : undefined),
    template_checks: buildTemplateChecksPayload({ sections, closesWork, robotOutcomes }),
    asset_outcomes: buildAssetOutcomesPayload({ sections, closesWork, followUpPerRobot, robotOutcomes }),
    daily_van_check: isSiteWork ? buildDailyVanCheckPayload(dailyVanCheck) : null,
    travel_record_id: travelRecord?.travel_id || undefined,
    // One robot (or none): the job-level follow-up, as before. Several
    // robots (Phase 6 step 4): each robot's follow-up travels in its
    // asset_outcomes entry, and none of the job-level fields is sent.
    ...(followUpPerRobot
      ? {
        follow_up_ticket_required: false,
        follow_up_subject: undefined,
        follow_up_description: undefined,
        follow_up_priority: undefined,
        create_follow_up_ticket: undefined,
        follow_up_asset_id: undefined
      }
      : {
        follow_up_priority: formData.follow_up_priority || undefined,
        create_follow_up_ticket: formData.follow_up_ticket_required || undefined,
        follow_up_asset_id: undefined
      }),
    // Phase 4: the crew, sent only when there is one, and the second
    // van only when the crew outgrew the first van's seats.
    additional_engineer_ids: crewIds.length > 0 ? crewIds : undefined,
    second_van: (isSiteWork && needsSecondVan)
      ? {
        vehicle_id: secondVan.vehicle_id,
        driver_id: secondVan.driver_id,
        ...(secondVanCheckOpen
          ? { daily_van_check: buildDailyVanCheckPayload({ ...secondVanCheck, vehicle_id: secondVan.vehicle_id }) }
          : {})
      }
      : undefined
  };

  // Carry on tomorrow: the check forms and robot answers wait for the day the
  // job is finished, so none are filed and no robot is settled today.
  if (carryOn) {
    body.assign_next_type = 'carry_on';
    body.template_checks = undefined;
    body.asset_outcomes = undefined;
  }

  // Filing a saved Log Work: say which one, and the versions and files the
  // screen had, so the server can refuse when a colleague changed it since.
  if (draftId) {
    body.log_work_draft_id = draftId;
    body.log_work_versions = versions && typeof versions === 'object' && !Array.isArray(versions) ? { ...versions } : {};
    body.log_work_file_ids = Array.isArray(fileIds) ? [...fileIds] : [];
    dropPhotosWithNoFile(body);
  }

  return body;
}

/** A photo with neither a link nor its content (removed after it failed) is never filed. */
const hasFile = (item) => Boolean(item && (item.url || item.data || item.dataUrl));
const keptPhotos = (list) => (Array.isArray(list) ? list.filter(hasFile) : list);

function dropPhotosWithNoFile(body) {
  if (Array.isArray(body.attachments)) body.attachments = keptPhotos(body.attachments);
  if (Array.isArray(body.template_checks)) {
    body.template_checks = body.template_checks.map(check => (check && Array.isArray(check.attachments)
      ? { ...check, attachments: keptPhotos(check.attachments) }
      : check));
  }
  if (body.daily_van_check && Array.isArray(body.daily_van_check.damage_photo_attachments)) {
    body.daily_van_check = {
      ...body.daily_van_check,
      damage_photo_attachments: keptPhotos(body.daily_van_check.damage_photo_attachments)
    };
  }
  const second = body.second_van && body.second_van.daily_van_check;
  if (second && Array.isArray(second.damage_photo_attachments)) {
    body.second_van = {
      ...body.second_van,
      daily_van_check: { ...second, damage_photo_attachments: keptPhotos(second.damage_photo_attachments) }
    };
  }
}

export default buildLogWorkPayload;
